import React, { useState } from "react";
import { Icon } from "../components/Icon";
import { WebMascot } from "../components/WebMascot";
import { WebVideoArt } from "../components/WebVideoArt";
import { ChildWithProgress, NowPlaying } from "../WebShell";

type Props = {
  child: ChildWithProgress | null;
  nowPlaying: NowPlaying | null;
  onStop: () => void;
};


function fmtClock(sec?: number) {
  if (!sec || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function WebCoWatch({ child, nowPlaying, onStop }: Props) {

  if (!nowPlaying) {
    return (
      <div
        className="fadeup w-resppad"
        style={{
          padding: 64,
          display: "grid",
          placeItems: "center",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: 420 }}>
          <div className="bob">
            <WebMascot mood="happy" color="#FF8A4C" size={140} />
          </div>
          <div
            style={{
              fontFamily: "var(--display)",
              fontSize: 28,
              fontWeight: 900,
              color: "var(--ink)",
              marginTop: 18,
            }}
          >
            Nothing playing right now
          </div>
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: "var(--ink-2)",
              marginTop: 8,
            }}
          >
            When your child starts a video on TV, co-watch will mirror it here so
            you can follow along and check in.
          </div>
        </div>
      </div>
    );
  }

  const theme = child?.theme ?? { color: "#FF8A4C", bg: "#FFE2C2" };
  const childName = child?.name ?? "your child";
  const progress = Math.max(0, Math.min(1, nowPlaying.progress ?? 0));
  const secondsWatched = nowPlaying.secondsWatched ?? 0;
  const duration = nowPlaying.durationSec ?? 0;
  const todayMin = child?.progress
    ? Math.round(child.progress.todayWatchedSeconds / 60)
    : 0;
  const dailyLimit = child?.controls?.dailyMinutes ?? 60;
  const aq = nowPlaying.activeQuestion;

  return (
    <div className="w-two">
      <div>
        <div
          style={{
            aspectRatio: "16 / 9",
            borderRadius: 18,
            overflow: "hidden",
            background: "#0a0a0e",
            position: "relative",
            border: "1px solid var(--line)",
          }}
        >
          <WebVideoArt category={nowPlaying.videoCategory ?? "Numbers"} />
          <div
            style={{
              position: "absolute",
              bottom: 60,
              left: "50%",
              transform: "translateX(-50%)",
              opacity: aq ? 0.3 : 1,
              filter: aq ? "blur(2px)" : "none",
              transition: "opacity 240ms",
            }}
          >
            <div className={nowPlaying.paused ? "" : "bob"}>
              <WebMascot
                mood={aq ? "curious" : "happy"}
                color="#FF8A4C"
                size={140}
              />
            </div>
          </div>

          {/* LIVE pill */}
          <div
            style={{
              position: "absolute",
              top: 14,
              left: 14,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 12px",
              borderRadius: 999,
              background: "rgba(0,0,0,0.55)",
              color: "#fff",
              fontSize: 12,
              fontWeight: 800,
              backdropFilter: "blur(6px)",
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#FF6B6B",
                boxShadow: "0 0 0 4px rgba(255,107,107,0.3)",
              }}
            />
            LIVE · Living Room TV
            {nowPlaying.paused && (
              <span style={{ opacity: 0.8, marginLeft: 4 }}>· PAUSED</span>
            )}
          </div>

          {/* Live question card — shown WHILE the kid is being asked.
              Parent sees the prompt and the child's selection as it lands. */}
          {aq && (
            <div
              className="fadeup"
              style={{
                position: "absolute",
                inset: 0,
                display: "grid",
                placeItems: "center",
                padding: 20,
              }}
            >
              <div
                style={{
                  width: "min(520px, 92%)",
                  background: "#fff",
                  borderRadius: 22,
                  padding: "20px 22px 22px",
                  border: "3px solid var(--ink)",
                  boxShadow: "0 24px 50px rgba(0,0,0,0.4)",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 900,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "var(--primary)",
                    marginBottom: 6,
                  }}
                >
                  Quick question · live
                </div>
                <div
                  style={{
                    fontFamily: "var(--display)",
                    fontSize: 22,
                    fontWeight: 900,
                    color: "var(--ink)",
                    lineHeight: 1.15,
                    marginBottom: 14,
                  }}
                >
                  {aq.prompt}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  {aq.options.map((opt, i) => {
                    const isCorrect = i === aq.correct;
                    const isPicked = aq.selectedOption === i;
                    const resolved = aq.selectedOption !== undefined;
                    let bg = "var(--bg-warm)";
                    let color = "var(--ink)";
                    if (resolved) {
                      if (isCorrect) {
                        bg = "var(--success)";
                        color = "#fff";
                      } else if (isPicked) {
                        bg = "#FF7A7A";
                        color = "#fff";
                      } else {
                        bg = "rgba(0,0,0,0.04)";
                        color = "var(--ink-3)";
                      }
                    }
                    return (
                      <div
                        key={i}
                        style={{
                          padding: "10px 14px",
                          borderRadius: 10,
                          background: bg,
                          color,
                          fontSize: 15,
                          fontWeight: 800,
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <span
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            background: "rgba(255,255,255,0.6)",
                            color: isPicked ? "var(--primary)" : "var(--ink)",
                            display: "grid",
                            placeItems: "center",
                            fontSize: 11,
                            fontWeight: 900,
                          }}
                        >
                          {String.fromCharCode(65 + i)}
                        </span>
                        <span style={{ flex: 1 }}>{opt}</span>
                        {resolved && isCorrect && <Icon name="check" size={16} />}
                      </div>
                    );
                  })}
                </div>
                {aq.selectedOption !== undefined && (
                  <div
                    className="fadeup"
                    style={{
                      marginTop: 10,
                      fontSize: 13,
                      fontWeight: 800,
                      color: aq.childAnsweredCorrectly
                        ? "var(--success)"
                        : "var(--ink-2)",
                    }}
                  >
                    {aq.childAnsweredCorrectly
                      ? `🎉 ${childName} got it right!`
                      : `Almost — ${childName} picked option ${String.fromCharCode(
                          65 + (aq.selectedOption ?? 0),
                        )}.`}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Progress + Stop */}
          <div
            style={{
              position: "absolute",
              left: 16,
              right: 16,
              bottom: 16,
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 14px",
              borderRadius: 12,
              background: "rgba(0,0,0,0.55)",
              color: "#fff",
              backdropFilter: "blur(6px)",
            }}
          >
            <div style={{ flex: 1, fontSize: 13, fontWeight: 700, minWidth: 0 }}>
              <div
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {nowPlaying.videoTitle}
              </div>
              <div
                style={{
                  height: 4,
                  background: "rgba(255,255,255,0.25)",
                  borderRadius: 4,
                  marginTop: 6,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${progress * 100}%`,
                    height: "100%",
                    background: "var(--star)",
                    transition: "width 400ms linear",
                  }}
                />
              </div>
            </div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                opacity: 0.85,
                minWidth: 92,
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {fmtClock(secondsWatched)}
              {duration ? ` / ${fmtClock(duration)}` : ""}
            </div>
            <button
              onClick={onStop}
              style={{
                border: 0,
                padding: "8px 14px",
                borderRadius: 10,
                background: "#FF7A7A",
                color: "#fff",
                fontWeight: 800,
                fontSize: 12,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Stop on TV
            </button>
          </div>
        </div>

      </div>

      <aside style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="w-panel">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: theme.bg,
                overflow: "hidden",
                display: "grid",
                placeItems: "center",
              }}
            >
              <div style={{ marginTop: 8 }}>
                <WebMascot mood="happy" color={theme.color} size={40} />
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "var(--ink-3)",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
              >
                Watching now
              </div>
              <div style={{ fontWeight: 900, fontSize: 17, color: "var(--ink)" }}>
                {childName}
              </div>
            </div>
          </div>
          <div
            className="w-respgrid2"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginTop: 16,
            }}
          >
            <div className="w-stat" style={{ padding: 14 }}>
              <div className="label" style={{ fontSize: 10 }}>
                Today
              </div>
              <div className="value" style={{ fontSize: 24 }}>
                {todayMin}
                <span
                  style={{ fontSize: 14, fontWeight: 700, color: "var(--ink-2)" }}
                >
                  {" "}
                  / {dailyLimit}m
                </span>
              </div>
            </div>
            <div className="w-stat" style={{ padding: 14 }}>
              <div className="label" style={{ fontSize: 10 }}>
                Stars today
              </div>
              <div className="value" style={{ fontSize: 24 }}>
                {child?.progress?.correctAnswers ?? 0}
              </div>
            </div>
          </div>
        </div>

        <div className="w-panel">
          <h3>Coach tip</h3>
          <div
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: "var(--ink-2)",
              lineHeight: 1.5,
            }}
          >
            When the question appears, pause and ask:{" "}
            <em>&quot;What if we had one more?&quot;</em> Wait for an answer; resist
            filling silence.
          </div>
        </div>
      </aside>
    </div>
  );
}

export default WebCoWatch;
